import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LinkedInHubHeader from './linkedin-hub/LinkedInHubHeader';
import PersonalProfileTab from './linkedin-hub/PersonalProfileTab';
import BusinessProfileTab from './linkedin-hub/BusinessProfileTab';

const LinkedInHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<'personal' | 'business'>('personal');

  // Parse hash from URL to set active tab
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (hash === 'business') {
      setActiveTab('business');
    } else {
      setActiveTab('personal');
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tab: 'personal' | 'business') => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <LinkedInHubHeader />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('personal')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'personal'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'personal' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Personal Profile</span>
              </button>
              
              <button
                onClick={() => handleTabChange('business')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                  ${activeTab === 'business'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'business' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>Business Profile</span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'personal' && <PersonalProfileTab />}
            {activeTab === 'business' && <BusinessProfileTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInHub;
